import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import Select from 'components/inputs/select';
import Text from 'components/inputs/text';
import Textarea from 'components/inputs/textarea';
import InfoTooltip from 'components/layout/info-tooltip';
import { ChatbotSubAgentToolType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import { confirmationModal, errorModal, successModal } from 'utils/ui';

const EMPTY_TOOL: Omit<ChatbotSubAgentToolType, 'id'> = {
  subAgentId: 0,
  toolName: '',
  description: '',
  additionalInformation: null,
  refetchTargets: null
};

export default function ChatbotSubAgentToolsPage() {
  const dispatch = useAppDispatch();
  const { chatbotSubAgentTools, chatbotSubAgents, mcpToolNames } =
    useAppSelector((state) => state.admin);

  const [view, setView] = useState<'table' | 'detail'>('table');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [agentFilter, setAgentFilter] = useState<string>('');

  const [subAgentId, setSubAgentId] = useState<number>(0);
  const [toolName, setToolName] = useState('');
  const [description, setDescription] = useState('');
  const [additionalInformation, setAdditionalInformation] = useState('');
  const [refetchTargets, setRefetchTargets] = useState('');

  const selectedTool =
    chatbotSubAgentTools.find((t) => t.id === selectedId) ?? null;

  const filteredTools = useMemo(() => {
    let tools = chatbotSubAgentTools;
    if (agentFilter) {
      tools = tools.filter((t) => t.subAgentId === Number(agentFilter));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      tools = tools.filter(
        (t) =>
          t.toolName.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q)
      );
    }
    return tools;
  }, [chatbotSubAgentTools, agentFilter, searchQuery]);

  const loadTool = (tool: ChatbotSubAgentToolType | null) => {
    if (tool) {
      setSubAgentId(tool.subAgentId);
      setToolName(tool.toolName);
      setDescription(tool.description);
      setAdditionalInformation(tool.additionalInformation ?? '');
      setRefetchTargets(tool.refetchTargets ?? '');
    } else {
      setSubAgentId(chatbotSubAgents[0]?.id ?? 0);
      setToolName(EMPTY_TOOL.toolName);
      setDescription(EMPTY_TOOL.description);
      setAdditionalInformation('');
      setRefetchTargets('');
    }
  };

  useEffect(() => {
    if (isCreating) {
      loadTool(null);
    } else {
      loadTool(selectedTool);
    }
  }, [selectedId, isCreating, chatbotSubAgentTools]);

  const handleSelect = (id: number) => {
    setIsCreating(false);
    setSelectedId(id);
    setView('detail');
  };

  const handleNew = () => {
    setSelectedId(null);
    setIsCreating(true);
    loadTool(null);
    setView('detail');
  };

  const handleBack = () => {
    setView('table');
    setSelectedId(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (!subAgentId || !toolName.trim() || !description.trim()) {
      errorModal('Sub-agent, tool name, and description are required.');
      return;
    }

    const payload = {
      subAgentId,
      toolName: toolName.trim(),
      description: description.trim(),
      additionalInformation: additionalInformation.trim() || null,
      refetchTargets: refetchTargets.trim() || null
    };

    try {
      if (isCreating) {
        await axios.post('chatbot-sub-agent-tool', payload);
        dispatch(fetchDefaultAdminData());
        successModal('Tool created.');
      } else if (selectedId) {
        await axios.patch('chatbot-sub-agent-tool', {
          id: selectedId,
          ...payload
        });
        dispatch(fetchDefaultAdminData());
        successModal('Tool updated.');
      }
      setIsCreating(false);
      setSelectedId(null);
      setView('table');
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    confirmationModal(
      'Are you sure you want to reset? All unsaved changes will be lost.',
      () => {
        if (isCreating) loadTool(null);
        else loadTool(selectedTool);
      }
    );
  };

  const handleDelete = () => {
    if (!selectedId) return;
    confirmationModal(
      `Are you sure you want to delete "${selectedTool?.toolName}"? This cannot be undone.`,
      async () => {
        try {
          await axios.delete(`chatbot-sub-agent-tool/${selectedId}`);
          setSelectedId(null);
          setView('table');
          dispatch(fetchDefaultAdminData());
          successModal('Tool deleted.');
        } catch {
          errorModal('Something went wrong.');
        }
      }
    );
  };

  const getAgentName = (aid: number) =>
    chatbotSubAgents.find((a) => a.id === aid)?.name ?? '—';

  if (view === 'detail') {
    return (
      <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
        <div className='flex items-center gap-3'>
          <button
            type='button'
            onClick={handleBack}
            className='flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700'
          >
            <ArrowLeftIcon className='h-4 w-4' />
            Back to Tools
          </button>
        </div>

        <div className='text-2xl font-bold text-gray-900'>
          {isCreating ? 'New Tool' : selectedTool?.toolName ?? 'Edit Tool'}
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>
              Sub-Agent
            </label>
            <Select
              value={subAgentId}
              onChange={(v) => setSubAgentId(Number(v))}
              options={chatbotSubAgents.map((a) => ({
                label: a.name,
                value: a.id
              }))}
              preventDeselection
            />
          </div>

          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>
              Tool Name
            </label>
            <Select
              value={toolName}
              onChange={(v) => setToolName(v.toString())}
              options={mcpToolNames.map((name) => ({
                label: name,
                value: name
              }))}
              preventDeselection
            />
          </div>
        </div>

        <div className='flex flex-col gap-1'>
          <label className='flex items-center gap-1 text-sm font-semibold text-gray-700'>
            Description
            <InfoTooltip>
              Instructions to the sub agent on when and how to use this tool.
            </InfoTooltip>
          </label>
          <Textarea
            value={description}
            onChange={(v) => setDescription(v)}
            placeholder='What this tool does'
            rows={3}
          />
        </div>

        <div className='flex flex-col gap-1'>
          <label className='flex items-center gap-1 text-sm font-semibold text-gray-700'>
            Post Call Context
            <InfoTooltip>
              This information will be provided to the AI model along side the
              tool call results.
            </InfoTooltip>
          </label>
          <Textarea
            value={additionalInformation}
            onChange={(v) => setAdditionalInformation(v)}
            placeholder='Optional extra context for the tool'
            rows={3}
          />
        </div>

        <div className='flex flex-col gap-1'>
          <label className='flex items-center gap-1 text-sm font-semibold text-gray-700'>
            Refetch Targets
            <InfoTooltip>
              Instructs the frontend of data that is affected. Optional. Comma
              separated list. Eligible values: configuration or structure
            </InfoTooltip>
          </label>
          <Text
            value={refetchTargets}
            onChange={(v) => setRefetchTargets(v)}
            placeholder='Comma-separated refetch targets'
          />
        </div>

        <div className='flex items-center gap-3 border-t border-gray-200 pt-4'>
          <Button
            text={isCreating ? 'Create Tool' : 'Save Changes'}
            color='green'
            onClick={handleSave}
            disabled={!toolName.trim() || !description.trim()}
          />
          <Button text='Reset' color='white' onClick={handleReset} />
          {!isCreating && selectedId && (
            <div className='ml-auto'>
              <Button text='Delete Tool' color='red' onClick={handleDelete} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>Sub-Agent Tools</div>

      <div className='flex items-end gap-3'>
        <div className='w-72'>
          <Text
            value={searchQuery}
            onChange={(v) => setSearchQuery(v)}
            placeholder='Search tools...'
            controlled
          />
        </div>
        <div className='w-56'>
          <Select
            value={agentFilter}
            onChange={(v) => setAgentFilter(v.toString())}
            options={[
              { label: 'All Sub-Agents', value: '' },
              ...chatbotSubAgents.map((a) => ({
                label: a.name,
                value: a.id
              }))
            ]}
          />
        </div>
        <div className='ml-auto'>
          <Button
            text='New Tool'
            color='green'
            onClick={handleNew}
            icon={PlusIcon}
          />
        </div>
      </div>

      <div className='max-h-[65vh] overflow-auto'>
        <table className='min-w-full border-separate border-spacing-0 divide-y divide-gray-300'>
          <thead>
            <tr>
              {['Sub-Agent', 'Tool Name', 'Description'].map((header) => (
                <th
                  key={header}
                  scope='col'
                  className='sticky top-0 z-10 border-b border-gray-300 bg-gray-100 px-3 pb-3.5 pt-3.5 text-left text-sm font-semibold text-gray-900'
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {filteredTools.map((tool) => (
              <tr
                key={tool.id}
                onClick={() => handleSelect(tool.id)}
                className='cursor-pointer hover:bg-gray-50'
              >
                <td className='whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900'>
                  {getAgentName(tool.subAgentId)}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {tool.toolName}
                </td>
                <td className='max-w-[500px] truncate px-3 py-3 text-sm text-gray-500'>
                  {tool.description}
                </td>
              </tr>
            ))}
            {filteredTools.length === 0 && (
              <tr>
                <td
                  colSpan={3}
                  className='py-8 text-center text-sm text-gray-400'
                >
                  No tools found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='text-sm text-gray-400'>
        {filteredTools.length} tool{filteredTools.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
